import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Button,
  Badge,
  Space,
  Spin,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Collapse,
  Row,
  Col,
  TableColumnsType,
  message,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { Asset, AssetsResponse, AssetStatus, ASSET_STATUS_LABELS, CreateAssetDto, ValuationBasis } from '../../types';
import { AssetService } from './asset.service';
import AssetDetails from './assetdetail';
import { useGlobalContext } from '../../store/GlobalContext';
import ImportExportModal from '../../components/ImportExportModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

/** Only an open asset is live. The other two have left the estate. */
const STATUS_BADGE: Record<AssetStatus, 'processing' | 'default' | 'success'> = {
  [AssetStatus.OPEN]: 'processing',
  [AssetStatus.DISTRIBUTED]: 'success',
  [AssetStatus.CLOSED]: 'default',
};

// Mapping of form field names to their corresponding panel keys
const fieldToPanel: { [key: string]: string } = {
  // Panel 1: Asset Identifiers
  name: '1',
  category: '1',
  valuationBasis: '1',
  originalMarketValue: '1',
  ref: '1',
  itemNumber: '1',
  marketValue: '1',

  // Panel 2: Asset Details
  location: '2',
  description: '2',
  secondaryIdentifier: '2',

  // Panel 3: Asset Values and Features
  coveredInWill: '3',
  jointlyOwned: '3',
  EPTLExemption: '3',
  eptlCategory: '3',
  isSmallEstate: '3',

  // Panel 4: Portfolio
  propertyType: '4',
  equity: '4',
  ticker: '4',
  originalQuantityOwned: '4',
  priorDayClosingPrice: '4',
  currentQuantityOwned: '4',
  dateOfPurchase: '4',
  costBasis: '4',

  // Panel 5: Lease Details
  lease: '5',

  // Panel 6: Liens and Financials
  liens: '6',
  statementValue: '6',
  netOfLien: '6',
  valueType: '6',
  valueSource: '6',

  // Panel 7: Status and Discounts
  status: '7',
  discountToMarket: '7',
  familyDiscount: '7',

  // Panel 8: Distribution Details
  internalCashlessDistribution: '8',
  internalCashlessDistributionAmount: '8',
  internalCashDistribution: '8',
  internalCashDistributionAmount: '8',

  // Panel 9: Estate Values
  estateValueIfInWill: '9',
  estateValueIfJointlyOwned: '9',

  // Panel 10: Additional Information
  percentageAwayFromEstValue: '10',
  percentJointlyOwned: '10',
  portfolioId: '10',
  internalDistributionAmount: '10',
  liabilityBalance: '10',

  // Panel 11: Sale and Donation
  sold: '11',
  cashReceived: '11',
  donate: '11',
};


/**
 * Suggests a valuation basis from the asset category.
 *
 * Only a default — the two do not map cleanly. In the source spreadsheet a
 * single category holds several bases: "Personal Property-Financial" covers
 * quoted stocks, a pension statement, an annual loan statement and an attested
 * life policy. Likewise cash, bitcoin and a shotgun are all attested despite
 * sitting in different categories. So the category picks the likely answer and
 * the user can correct it.
 */
/** Money renderer that tolerates a missing value rather than throwing. */
const money = (value?: number | string | null): string =>
  value == null || value === '' ? '—' : `$${Number(value).toLocaleString()}`;

/** Short labels for the valuation bases, matching the backend enum. */
const BASIS_SHORT: Record<string, string> = {
  market_quoted: 'Market quote',
  attested: 'Stated',
  appraised: 'Appraised',
  statement: 'Statement',
};

export function defaultBasisForCategory(category?: string): ValuationBasis {
  switch (category) {
    case 'Bonds':
    case 'Equity':
    case 'Investment':
      return ValuationBasis.MARKET_QUOTED;
    case 'Cash':
      return ValuationBasis.ATTESTED;
    case 'FinancialAsset':
      // Deliberately not market_quoted: this category also holds insurance
      // policies and pensions. Attested is the safer starting point.
      return ValuationBasis.ATTESTED;
    default:
      // RealEstate, House, Firearm, FarmTool, Other — all appraised.
      return ValuationBasis.APPRAISED;
  }
}

const AssetsListPage: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalAssets, setTotalAssets] = useState<number>(0);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [totalValueLastMonth, setTotalValueLastMonth] = useState<number>(0);
  const [averageAssetValue, setAverageAssetValue] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState<boolean>(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState<boolean>(false);
  const [createForm] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorPanels, setErrorPanels] = useState<string[]>([]); // Tracks panels with errors
  const [activePanelKey, setActivePanelKey] = useState<string | undefined>(undefined); // Controls which panel is open
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [downloadFromDate, setDownloadFromDate] = useState<string | null>(null);
  const [downloadToDate, setDownloadToDate] = useState<string | null>(null);

  const financialTypes = new Set(['Bonds', 'Equity', 'FinancialAsset', 'Loans']);;

  const { selectedEstate } = useGlobalContext();

  const estateId = selectedEstate?.uuid || ''; // Replace with dynamic value as needed

  useEffect(() => {
    if (estateId) {
      fetchAssets(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, estateId]);

  const fetchAssets = async (page: number, limit: number) => {
    setLoading(true);
    setError('');
    try {
      const response: AssetsResponse = await AssetService.fetchAssets({ estateId, page, limit });
      setAssets(response.data);
      setTotalAssets(response.total);
      setTotalValue(response.totalValue);
      setTotalValueLastMonth(response.totalValueLastMonth);
      setAverageAssetValue(response.averageAssetValue);
      setCurrentPage(response.page);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch assets.');
    } finally {
      setLoading(false);
    }
  };

  const showAssetDetails = (asset: Asset) => {
    setSelectedAsset(asset);
    setIsDetailsModalVisible(true);
  };

  const closeAssetDetails = () => {
    setSelectedAsset(null);
    setIsDetailsModalVisible(false);
  };

  const showCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
    setErrorPanels([]);
    setActivePanelKey(undefined);
    setIsEditing(false);
  };

  const handleEditAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    createForm.setFieldsValue({
      // Panel 1: Asset Identifiers
      name: asset.name || '',
      category: asset.category || '',
      valuationBasis: asset.valuationBasis ?? defaultBasisForCategory(asset.category),
      originalMarketValue:
        asset.originalMarketValue != null ? Number(asset.originalMarketValue) : undefined,
      ref: asset.ref || '',
      itemNumber: asset.itemNumber || '',
      marketValue: asset.marketValue != null ? Number(asset.marketValue) : undefined,
  
      // If you also have estimatedValue / realizedValue in Panel 1:
      // estimatedValue: asset.estimatedValue != null ? Number(asset.estimatedValue) : undefined,
      // realizedValue: asset.realizedValue != null ? Number(asset.realizedValue) : undefined,
  
      // Panel 2: Asset Details
      location: asset.location || '',
      description: asset.description || '',
      secondaryIdentifier: asset.secondaryIdentifier || '',
  
      // Panel 3: Asset Values and Features
      coveredInWill: asset.coveredInWill || false,
      jointlyOwned: asset.jointlyOwned || false,
      EPTLExemption: asset.EPTLExemption || false,
      eptlCategory: asset.eptlCategory ?? undefined,
      isSmallEstate: asset.isSmallEstate || false,
  
      // Panel 4: Portfolio
      // propertyType: asset.propertyType || '',
      equity: asset.portfolio?.equity || '',
      ticker: asset.portfolio?.ticker || '',
      originalQuantityOwned:
        asset.portfolio?.originalQuantityOwned != null ? Number(asset.portfolio?.originalQuantityOwned) : undefined,
      priorDayClosingPrice:
        asset.portfolio?.priorDayClosingPrice != null ? Number(asset.portfolio?.priorDayClosingPrice) : undefined,
      currentQuantityOwned:
        asset.portfolio?.currentQuantityOwned != null ? Number(asset.portfolio?.currentQuantityOwned) : undefined,
      dateOfPurchase:  asset.portfolio?.dateOfPurchase != null? asset.portfolio?.dateOfPurchase : '',
      costBasis: asset.portfolio?.costBasis != null ? Number(asset.portfolio?.costBasis) : undefined,
  
      // Panel 5: Lease Details
      lease: asset.lease || false,
  
      // Panel 6: Liens and Financials
      liens: asset.liens != null ? Number(asset.liens) : undefined,
      statementValue: asset.statementValue != null ? Number(asset.statementValue) : undefined,
      netOfLien: asset.netOfLien != null ? Number(asset.netOfLien) : undefined,
      valueType: asset.valueType || '',
      valueSource: asset.valueSource || '',
  
      // Panel 7: Status and Discounts
      status: asset.status || AssetStatus.OPEN,
      discountToMarket:
        asset.discountToMarket != null ? Number(asset.discountToMarket) : undefined,
      familyDiscount:
        asset.familyDiscount != null ? Number(asset.familyDiscount) : undefined,
  
      // Panel 8: Distribution Details
      internalCashlessDistribution: asset.internalCashlessDistribution || false,
      internalCashlessDistributionAmount:
        asset.internalCashlessDistributionAmount != null
          ? Number(asset.internalCashlessDistributionAmount)
          : undefined,
      internalCashDistribution: asset.internalCashDistribution || false,
      internalCashDistributionAmount:
        asset.internalCashDistributionAmount != null
          ? Number(asset.internalCashDistributionAmount)
          : undefined,
  
      // Panel 9: Estate Values
      estateValueIfInWill:
        asset.estateValueIfInWill != null ? Number(asset.estateValueIfInWill) : undefined,
      estateValueIfJointlyOwned:
        asset.estateValueIfJointlyOwned != null ? Number(asset.estateValueIfJointlyOwned) : undefined,
  
      // Panel 10: Additional Information
      percentageAwayFromEstValue:
        asset.percentageAwayFromEstValue != null ? Number(asset.percentageAwayFromEstValue) : undefined,
      percentJointlyOwned:
        asset.percentJointlyOwned != null ? Number(asset.percentJointlyOwned) : undefined,
      portfolioId: asset.portfolioId != null ? Number(asset.portfolioId) : undefined,
      internalDistributionAmount:
        asset.internalDistributionAmount != null ? Number(asset.internalDistributionAmount) : undefined,
      liabilityBalance:
        asset.liabilityBalance != null ? Number(asset.liabilityBalance) : undefined,
  
      // Panel 11: Sale and Donation
      sold: asset.sold || false,
      cashReceived: asset.cashReceived != null ? Number(asset.cashReceived) : undefined,
      donate: asset.donate || false,
    });
    setIsCreateModalVisible(true);
    setIsEditing(true);
  };

  const handleDeleteAsset = async (assetId: string) => {
    try {
      await AssetService.deleteAsset(estateId, assetId);
      message.success('Asset deleted successfully');
      // Refresh the assets list
      fetchAssets(currentPage, pageSize);
      // Close the asset details modal
      closeAssetDetails();
    } catch (error) {
      console.error('Error deleting asset:', error);
      message.error('Failed to delete asset.');
    }
  };

  const handleCreateAsset = async (values: any) => {
    setSubmitting(true);
    try {
      const createAssetDto: CreateAssetDto = {
        ...values,
      };
      if (isEditing && selectedAsset) {

        // Update the asset
        await AssetService.updateAsset(estateId, selectedAsset.uid, createAssetDto);
        message.success('Asset updated successfully');
      } else {
        // Create new asset
        await AssetService.createAsset(estateId, createAssetDto);
        message.success('Asset created successfully');
      }
      closeCreateModal();
      fetchAssets(currentPage, pageSize); // Refresh assets list
    } catch (err: any) {
      console.error(err);
      if (err.response && err.response.status === 400 && Array.isArray(err.response.data.message)) {
        const errorFields = err.response.data.message;
        const formErrors = errorFields.map((field: any) => ({
          name: field.property,
          errors: Object.values(field.constraints || {}).length > 0 ? Object.values(field.constraints) : ['Invalid value'],
        }));
        createForm.setFields(formErrors);

        // Determine which panels have errors
        const panelsWithErrors:any = Array.from(new Set(errorFields.map((field: any) => fieldToPanel[field.property] || '')));
        setErrorPanels(panelsWithErrors);

        // Automatically open the first panel with errors
        if (panelsWithErrors.length > 0) {
          setActivePanelKey(panelsWithErrors[0]);
        }
      } else {
        // Properly display error messages
        Modal.error({
          title: 'Failed to Create Asset',
          content: Array.isArray(err.response?.data?.message)
            ? (
              <ul>
                {err.response.data.message.map((e: any, index: number) => (
                  <li key={index}>{e.property}: {Object.values(e.constraints).join(', ')}</li>
                ))}
              </ul>
            )
            : err.message || 'An error occurred while creating the asset.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns: TableColumnsType<Asset> = [
    { title: 'ASSET ID', dataIndex: 'id', key: 'id' },
    {
      title: 'DATE ADDED',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    { title: 'REF', dataIndex: 'ref', key: 'ref', width: 100, fixed: 'left' },
    { title: 'ITEM NUMBER', dataIndex: 'itemNumber', key: 'itemNumber' },
    { title: 'MARKET VALUE', dataIndex: 'marketValue', key: 'marketValue', render: money },
    { title: 'ESTIMATED VALUE', dataIndex: 'estimatedValue', key: 'estimatedValue', render: money },
    { title: 'REALIZED VALUE', dataIndex: 'realizedValue', key: 'realizedValue', render: money },
    {
      title: 'VALUED BY',
      dataIndex: 'valuationBasis',
      key: 'valuationBasis',
      render: (value: string) => BASIS_SHORT[value] ?? '—',
    },
    { title: 'ASSET NAME', dataIndex: 'name', key: 'name' },
    { title: 'ASSET TYPE', dataIndex: 'category', key: 'category' },
    { title: 'GEO LOCATION', dataIndex: 'location', key: 'location' },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Badge
          status={STATUS_BADGE[status] ?? 'default'}
          text={ASSET_STATUS_LABELS[status] ?? status}
        />
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      render: (_: any, record: Asset) => (
        <Button type="link" onClick={() => showAssetDetails(record)}>
          View Details
        </Button>
      ),
    },
  ];

  

  // Watchers for conditional fields
  const internalCashlessDistribution = Form.useWatch('internalCashlessDistribution', createForm);
  const internalCashDistribution = Form.useWatch('internalCashDistribution', createForm);
  const jointlyOwned = Form.useWatch('jointlyOwned', createForm);
  const sold = Form.useWatch('sold', createForm);
  const propertyType = Form.useWatch('category', createForm);
  const valuationBasis = Form.useWatch('valuationBasis', createForm);
  // Portfolio fields belong to quote-driven assets only. Falls back to the
  // category default so an asset created before the basis existed still
  // shows the right fields.
  const isQuoted =
    (valuationBasis ?? defaultBasisForCategory(propertyType)) === ValuationBasis.MARKET_QUOTED;

  // Function to determine panel styles based on errors
  const getPanelStyles = (panelKey: string) => {
    if (errorPanels.includes(panelKey)) {
      return {
        headerStyle: { color: '#ff4d4f' }, // Red color for header
        style: { border: '1px solid #ff4d4f' }, // Red border for panel
      };
    }
    return {};
  };

  const downloadCsv = async () => {
    try {
      const csvData = await AssetService.downloadAssetsCsv(estateId, downloadFromDate, downloadToDate);

      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'assets.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading CSV:', error);
      message.error('Failed to download CSV.');
    }
  };


  const csvDownloadForm = (
    <Form layout="inline" >
      <Form.Item label="From">
        <DatePicker
          onChange={(date, dateString) => setDownloadFromDate(typeof dateString === 'string' ? dateString : null)}
          style={{ width: 150 }}
        />
      </Form.Item>
      <Form.Item label="To">
        <DatePicker
          onChange={(date, dateString) => setDownloadToDate(typeof dateString === 'string' ? dateString : null)}
          style={{ width: 150 }}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" onClick={downloadCsv}>Download CSV</Button>
      </Form.Item>
    </Form>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            style={{ textAlign: 'center', height: '100%' }}
            bordered={false}
          >
            <DollarCircleOutlined
              style={{ fontSize: '24px', color: '#1890ff' }}
            />
            <Title level={4}>${totalValue.toLocaleString()}</Title>
            <Text>Total Asset Value</Text>
            <div>
              Compare to last month{' '}
              {totalValueLastMonth > totalValue ? '↑' : '↓'}{' '}
              {totalValueLastMonth
                ? Math.abs(
                    ((totalValue - totalValueLastMonth) / totalValueLastMonth) *
                      100,
                  ).toFixed(2)
                : '0.00'}
              %
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            style={{ textAlign: 'center', height: '100%' }}
            bordered={false}
          >
            <CalendarOutlined
              style={{ fontSize: '24px', color: '#faad14' }}
            />
            <Title level={4}>{totalAssets}</Title>
            <Text>Assets Acquired</Text>
            <div>
              Compare to last month { /* Implement similar comparison if data is available */ }
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            style={{ textAlign: 'center', height: '100%' }}
            bordered={false}
          >
            <DollarCircleOutlined
              style={{ fontSize: '24px', color: '#52c41a' }}
            />
            <Title level={4}>${averageAssetValue.toLocaleString()}</Title>
            <Text>Average Asset Value</Text>
            <div>
              {/* Additional comparison or insights */}
            </div>
          </Card>
        </Col>
        {/* Add more summary cards as needed */}
      </Row>

      {/* Assets Table */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Assets List</span>
            <div style={{ display: 'flex', flexFlow: 'horizontal', alignItems: 'center', gap: '8px' }}>
            {csvDownloadForm}
            <Button
              icon={<FileExcelOutlined />}
              onClick={() => setIsImportModalVisible(true)}
            >
              Import Assets
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showCreateModal}
            >
              Add Asset
            </Button></div>
          </div>
        }
        style={{ marginTop: '24px' }}
        extra={null} // Remove the separate Add Asset button
      >
        <Text>
          View and manage all assets with details on status, location, and type.
        </Text>
        
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ margin: '16px 0' }}
          />
        )}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={assets}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalAssets,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </Spin>
      </Card>

      {/* Asset Details Modal */}
      <AssetDetails
        visible={isDetailsModalVisible}
        onClose={closeAssetDetails}
        asset={selectedAsset}
        onDelete={handleDeleteAsset}
        onEdit={handleEditAsset}
      />

      {/* Import Modal */}
      {estateId && (
        <ImportExportModal
          open={isImportModalVisible}
          onClose={() => setIsImportModalVisible(false)}
          estateId={estateId}
        />
      )}

      {/* Create Asset Modal */}
      <Modal
        title={isEditing ? 'Edit Asset' : 'Add New Asset'}
        open={isCreateModalVisible}
        onCancel={closeCreateModal}
        footer={null}
        destroyOnClose
        width={800}
        styles={{ body: { padding: '24px' } }}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateAsset}
          onValuesChange={(changedValues, allValues) => {
            // Auto-compute netOfLien. Mirrors calculateAssetValues() in
            // wzee-be's src/asset/asset-valuation.ts: a leased asset is not
            // owned so it contributes nothing, and an owned one always has its
            // lien deducted. Not floored at zero, so a lien larger than the
            // asset shows as the negative it is.
            if (
              changedValues.marketValue !== undefined ||
              changedValues.liens !== undefined ||
              changedValues.lease !== undefined
            ) {
              const { marketValue, liens, lease } = allValues;
              createForm.setFieldsValue({
                netOfLien: lease ? 0 : (marketValue || 0) - (liens || 0),
              });
            }

            // Handle propertyType changes
            if (changedValues.category) {
              // Suggest a valuation basis from the category so the common case
              // needs no extra input. It stays editable: a category does not
              // determine the basis on its own — a life policy and a share
              // holding are both "financial" but valued quite differently.
              createForm.setFieldsValue({
                valuationBasis: defaultBasisForCategory(changedValues.category),
              });

              if (!financialTypes.has(changedValues.category)) {
                createForm.setFieldsValue({
                  ticker: undefined,
                  priorDayClosingPrice: 0,
                  originalQuantityOwned: 1,
                  currentQuantityOwned: 1,
                });
                createForm.setFields([
                  { name: 'ticker', errors: [] },
                  { name: 'priorDayClosingPrice', errors: [] },
                ]);
              } else {
                // Optionally reset or enable fields if needed
              }
            }
          }}
        >
          <Collapse
            accordion
            activeKey={activePanelKey}
            onChange={(key:any) => setActivePanelKey(key)}
          >
            {/* Asset Identifiers */}
            <Panel
              header="Asset Identifiers"
              key="1"
              {...getPanelStyles('1')}
            >
              {/* Existing Fields */}
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the asset name' },
                ]}
              >
                <Input placeholder="Asset name.." />
              </Form.Item>

              <Form.Item
                label="Asset Type"
                name="category"
                rules={[
                  { required: isEditing? false : true, message: 'Please select the asset type' },
                ]}
              >
                <Select placeholder="Select asset type">
                  <Option value="Bonds">Bonds</Option>
                  <Option value="RealEstate">Real Estate</Option>
                  <Option value="FinancialAsset">Financial Asset</Option>
                  <Option value="Firearm">Firearm</Option>
                  <Option value="FarmTool">Farm Tool</Option>
                  <Option value="House">House</Option>
                  <Option value="Cash">Cash</Option>
                  <Option value="Investment">Investment</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="How is this valued?"
                name="valuationBasis"
                tooltip="Determines how the value is worked out. Suggested from the asset type, but a category does not decide it — a life policy and a share holding are both financial assets yet valued differently."
                rules={[{ required: isEditing ? false : true, message: 'Please select how this asset is valued' }]}
              >
                <Select placeholder="Select valuation basis">
                  <Option value={ValuationBasis.MARKET_QUOTED}>
                    Market quoted — quantity x market price (shares, funds)
                  </Option>
                  <Option value={ValuationBasis.ATTESTED}>
                    Attested — a stated amount (bank balance, policy, dealer quote)
                  </Option>
                  <Option value={ValuationBasis.APPRAISED}>
                    Appraised — an estimate (Kelly's, auction, valuer). Discountable.
                  </Option>
                  <Option value={ValuationBasis.STATEMENT}>
                    Statement — a periodic figure (pension, annual statement)
                  </Option>
                </Select>
              </Form.Item>

              {!isQuoted && (
                <Form.Item
                  label="Value"
                  name="originalMarketValue"
                  tooltip="The gross figure before any discount: the appraisal, balance or stated amount."
                  rules={[
                    { required: isEditing ? false : true, message: 'Please enter the value' },
                    { type: 'number', min: 0, message: 'Value must be a positive number' },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder="e.g. 450000" />
                </Form.Item>
              )}

              <Form.Item
                label="Reference (ref)"
                name="ref"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the reference' },
                  { type: 'string', message: 'ref must be a string' },
                ]}
              >
                <Input placeholder="Reference ID" />
              </Form.Item>

              <Form.Item
                label="Item Number"
                name="itemNumber"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the item number' },
                  { type: 'string', message: 'itemNumber must be a string' },
                ]}
              >
                <Input placeholder="Item Number" />
              </Form.Item>

              {/* Added marketValue Field */}
              <Form.Item
                label="Market Value"
                name="marketValue"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the market value' },
                  { type: 'number', min: 0, message: 'Market Value must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 50000" disabled={isEditing === true} />
              </Form.Item>
            </Panel>

            {/* Asset Details */}
            <Panel
              header="Asset Details"
              key="2"
              {...getPanelStyles('2')}
            >
              <Form.Item
                label="Geo Location"
                name="location"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the location' },
                ]}
              >
                <Input placeholder="Utopia Parkway, Auburndale, Queens, NY, USA" />
              </Form.Item>

              {/* Hidden Field for Location Details */}
              <Form.Item
                name="locationDetail"
                hidden
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the description' },
                ]}
              >
                <Input.TextArea rows={2} placeholder="Description" />
              </Form.Item>

              <Form.Item
                label="Secondary Identifier"
                name="secondaryIdentifier"
                rules={[
                  { required: false },
                ]}
              >
                <Input placeholder="Identifier such as TKT/Geo Coordinates/VIN/Image/Serial No." />
              </Form.Item>
            </Panel>

            {/* Asset Values and Features */}
            <Panel
              header="Asset Values and Features"
              key="3"
              {...getPanelStyles('3')}
            >
              <Form.Item
                name="coveredInWill"
                label="Is Asset Covered In Will?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="jointlyOwned"
                label="Is Asset Jointly Owned?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="eptlCategory"
                label="Claim as exempt family property?"
                tooltip="Property set aside for a surviving spouse or a child under 21, which does not go through probate. Each category has its own dollar limit that applies to the estate's total for that category — not to each item — so the limit may be reached across several items."
              >
                <Select allowClear placeholder="Not claimed">
                  <Option value="household">
                    Household goods, furniture, clothing, jewellery — $20,000 limit
                  </Option>
                  <Option value="books_media">
                    Books, family pictures, religious books, media — $2,500 limit
                  </Option>
                  <Option value="farm">
                    Farm and domestic animals, machinery, tractors — $20,000 limit
                  </Option>
                  <Option value="vehicle">One motor vehicle — $25,000 limit</Option>
                  <Option value="money">
                    Money, bank accounts, marketable securities — $25,000 limit
                  </Option>
                  <Option value="child_setoff">Set-off for a child under 21 — $10,000 limit</Option>
                </Select>
              </Form.Item>

              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Limits apply across the whole estate"
                description={
                  'Real estate can never be claimed — the exemption covers personal property only. ' +
                  'Whether an item ends up exempt depends on the estate total for its category, so the ' +
                  'Family Jewels summary is where the final figures and any warnings appear.'
                }
              />

              <Form.Item
                name="isSmallEstate"
                label="Is estate a small estate?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Panel>

            {/* Portfolio */}
            <Panel
              header="Portfolio/Property Values"
              key="4"
              {...getPanelStyles('4')}
            >

              <Form.Item
                label="Equity/Property"
                name="equity"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the equity/property' },
                ]}
              >
                <Input placeholder="10 Year Bond Funds.." />
              </Form.Item>

              

              {isQuoted && (
                <>
                  <Form.Item
                  label="Ticker (optional)"
                  name="ticker"
                  rules={[
                    { required: false },
                  ]}
                >

                  <Input placeholder="MMM"  />
                </Form.Item>

                <Form.Item
                label="Prior Day Closing Price"
                name="priorDayClosingPrice"
                tooltip="The asset is valued at quantity x this price. Cost basis is kept for gain/loss only."
                rules={[
                  { required: true, message: 'Please enter the prior day closing price' },
                  { type: 'number', min: 0, message: 'Prior Day Closing Price must be a positive number' },
                ]}
                >
                <InputNumber style={{ width: '100%' }} placeholder="" />
                </Form.Item>
              </>
              )}

              

              <Form.Item
                label="Original Quantity Owned"
                name="originalQuantityOwned"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the original quantity owned' },
                  { type: 'number', min: 0, message: 'Original Quantity Owned must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" />
              </Form.Item>

              

              <Form.Item
                label="Current Quantity Owned"
                name="currentQuantityOwned"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the current quantity owned' },
                  { type: 'number', min: 0, message: 'Current Quantity Owned must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" />
              </Form.Item>

              <Form.Item
                label="Date Of Purchase"
                name="dateOfPurchase"
                rules={[
                  { required: isEditing? false : true, message: 'Please select the date of purchase' },
                ]}
              >
                <Input type="date" />
              </Form.Item>

              <Form.Item
                label={financialTypes.has(propertyType)? "Cost Basis": "Original Purchase Price" }
                name="costBasis"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the cost basis' },
                  { type: 'number', min: 0, message: 'Cost Basis must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" />
              </Form.Item>
            </Panel>

            {/* Lease Details */}
            <Panel
              header="Lease Details"
              key="5"
              {...getPanelStyles('5')}
            >
              <Form.Item
                name="lease"
                label="Is on lease?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Panel>

            {/* Liens and Financials */}
            <Panel
              header="Liens and Financials"
              key="6"
              {...getPanelStyles('6')}
            >
              {/* One field used to ask for both of these at once, so it
                  collected statement figures rather than debts. They are now
                  asked for separately. */}
              <Form.Item
                label="Lien against asset"
                name="liens"
                tooltip="What is owed against this asset. Deducted from its value unless the asset is leased."
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the liens amount' },
                  { type: 'number', min: 0, message: 'Liens must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" />
              </Form.Item>

              <Form.Item
                label="Recent statement value"
                name="statementValue"
                tooltip="The latest figure from the bank or broker statement. Recorded for provenance; it does not change the valuation."
                rules={[
                  { type: 'number', min: 0, message: 'Statement value must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" />
              </Form.Item>

              {/* Net of Liens, auto computed. Shown for every asset: it used to
                  appear only for leases, which hid the deduction on exactly the
                  assets it now applies to. */}
              <Form.Item
                label="Net of Liens/Lease Value/Paid Up Value of LifeIns"
                name="netOfLien"
                tooltip="Market value less the lien. Zero for a leased asset, which is not owned. Negative when the lien exceeds the asset."
                rules={[{ type: 'number', message: 'Net of Liens must be a number' }]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="" disabled />
              </Form.Item>

              <Form.Item
                label="Value Type"
                name="valueType"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the value type' },
                ]}
              >
                <Input placeholder="i.e est" />
              </Form.Item>

              <Form.Item
                label="Value Source"
                name="valueSource"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the value source' },
                ]}
              >
                <Input placeholder="i.e Investing.com" />
              </Form.Item>
            </Panel>

            {/* Status and Discounts */}
            <Panel
              header="Status and Discounts"
              key="7"
              {...getPanelStyles('7')}
            >
              <Form.Item
                label="Status"
                name="status"
                tooltip="Distributed and closed both take the asset out of the remaining estate value. It stays on file for the court accounting."
                rules={[
                  { required: isEditing? false : true, message: 'Please select the status' },
                ]}
              >
                <Select placeholder="Select a status">
                  {Object.values(AssetStatus).map((value) => (
                    <Option key={value} value={value}>
                      {ASSET_STATUS_LABELS[value]}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Discount To Market (%)"
                name="discountToMarket"
                tooltip="Percentage off the market value, 0-100. Enter 30 for a 30% discount, not 0.3."
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the discount to market' },
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: 'Discount To Market must be a percentage between 0 and 100',
                  },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  addonAfter="%"
                  placeholder="e.g. 30"
                />
              </Form.Item>

              <Form.Item
                label="Discount To Family (%)"
                name="familyDiscount"
                tooltip="Percentage haircut applied when distributing to family, 0-100. Enter 30 for 30%, not 0.3."
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the discount to family' },
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: 'Discount To Family must be a percentage between 0 and 100',
                  },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  addonAfter="%"
                  placeholder="e.g. 30"
                />
              </Form.Item>
            </Panel>

            {/* Distribution Details */}
            <Panel
              header="Distribution Details"
              key="8"
              {...getPanelStyles('8')}
            >
              <Form.Item
                name="internalCashlessDistribution"
                label="Items earmarked for internal cashless distribution?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              {/* Internal Cashless Distribution Amount - Conditional */}
              {internalCashlessDistribution && (
                <Form.Item
                  label="Internal Cashless Distribution Amount"
                  name="internalCashlessDistributionAmount"
                  rules={[
                    {
                      required: internalCashlessDistribution,
                      message: 'Please enter the internal cashless distribution amount',
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: 'Internal Cashless Distribution Amount must be a positive number',
                    },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder="e.g., 10000" />
                </Form.Item>
              )}

              <Form.Item
                name="internalCashDistribution"
                label="Items earmarked for internal cash distribution?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              {/* Internal Cash Distribution Amount - Conditional */}
              {internalCashDistribution && (
                <Form.Item
                  label="Internal Cash Distribution Amount"
                  name="internalCashDistributionAmount"
                  rules={[
                    {
                      required: internalCashDistribution,
                      message: 'Please enter the internal cash distribution amount',
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: 'Internal Cash Distribution Amount must be a positive number',
                    },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder="e.g., 5000" />
                </Form.Item>
              )}
            </Panel>

            {/* Estate Values */}
            <Panel
              header="Estate Values"
              key="9"
              {...getPanelStyles('9')}
            >
              <Form.Item
                label="Estate Value If In Will"
                name="estateValueIfInWill"
                rules={[
                  { required: false },
                  { type: 'number', min: 0, message: 'Estate Value If In Will must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 70000" />
              </Form.Item>

              <Form.Item
                label="Estate Value If Jointly Owned"
                name="estateValueIfJointlyOwned"
                rules={[
                  { required: false },
                  { type: 'number', min: 0, message: 'Estate Value If Jointly Owned must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 80000" />
              </Form.Item>
            </Panel>

            {/* Additional Fields */}
            <Panel
              header="Additional Information"
              key="10"
              {...getPanelStyles('10')}
            >
              <Form.Item
                label="Percentage Away From Estimated Value"
                name="percentageAwayFromEstValue"
                rules={[
                  { required: false },
                  { type: 'number', min: 0, message: 'Percentage Away From Estimated Value must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 10" />
              </Form.Item>

              <Form.Item
                label="Percent Jointly Owned"
                name="percentJointlyOwned"
                rules={[
                  { required: false },
                  { type: 'number', min: 0, message: 'Percent Jointly Owned must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 50" />
              </Form.Item>

              <Form.Item
                label="Portfolio ID"
                name="portfolioId"
                rules={[
                  { required: isEditing? true : false, message: 'Please enter the portfolio ID' },
                  { type: 'number', min: 0, message: 'Portfolio ID must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 1" disabled={true}/>
              </Form.Item>

              <Form.Item
                label="Internal Distribution Amount"
                name="internalDistributionAmount"
                rules={[
                  { required: false },
                  { type: 'number', min: 0, message: 'Internal Distribution Amount must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 3000" />
              </Form.Item>

              <Form.Item
                label="Liability Balance"
                name="liabilityBalance"
                rules={[
                  { required: isEditing? false : true, message: 'Please enter the liability balance' },
                  { type: 'number', min: 0, message: 'Liability Balance must be a positive number' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 5000" />
              </Form.Item>
            </Panel>

            {/* Sale and Donation */}
            <Panel
              header="Sale and Donation"
              key="11"
              {...getPanelStyles('11')}
            >
              <Form.Item
                name="sold"
                label="Is Item Sold?"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              {/* Cash Received if Sold - Conditional */}
              {sold && (
                <Form.Item
                  label="Cash Received if Sold"
                  name="cashReceived"
                  rules={[
                    { required: sold, message: 'Please enter the cash received' },
                    { type: 'number', min: 0, message: 'Cash Received must be a positive number' },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} placeholder="e.g., 2000" />
                </Form.Item>
              )}

              <Form.Item
                name="donate"
                label="Donated/thrown away/Given To Friend"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Panel>
          </Collapse>

          {/* Submit Button */}
          <Form.Item style={{ marginTop: '24px' }}>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeCreateModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
            >
              {isEditing ? 'Update Asset' : 'Add Asset'}
            </Button>
          </Space>
        </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetsListPage;
